import React, { useState, useEffect } from 'react';
import './PhoneNumberModal.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
const PhoneNumberModal = ({ isOpen, onClose, onSave, phoneNumber = null, wabaId = null, loading = false }) => {
  const [formData, setFormData] = useState({
    phoneNumberId: '',
    phoneNumber: '',
    displayPhoneNumber: '',
    countryCode: '+1',
    accountMode: 'STANDARD',
    status: 'active',
    isPrimary: false,
    isDefault: false
  });
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');

  const isEditMode = !!phoneNumber;

  useEffect(() => {
    if (phoneNumber && isEditMode) {
      setFormData({
        phoneNumberId: phoneNumber.phoneNumberId || '',
        phoneNumber: phoneNumber.phoneNumber || '',
        displayPhoneNumber: phoneNumber.displayPhoneNumber || '',
        countryCode: phoneNumber.countryCode || '+1',
        accountMode: phoneNumber.accountMode || 'STANDARD',
        status: phoneNumber.status || 'active',
        isPrimary: phoneNumber.isPrimary || false,
        isDefault: phoneNumber.isDefault || false
      });
    } else {
      setFormData({
        phoneNumberId: '',
        phoneNumber: '',
        displayPhoneNumber: '',
        countryCode: '+1',
        accountMode: 'STANDARD',
        status: 'active',
        isPrimary: false,
        isDefault: false
      });
    }
    setErrors({});
  }, [phoneNumber, isOpen, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phoneNumber: value
    }));

    if (errors.phoneNumber) {
      setErrors(prev => ({ ...prev, phoneNumber: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validate phoneNumberId only in create mode (not in edit mode)
    if (!isEditMode && !formData.phoneNumberId) {
      newErrors.phoneNumberId = 'Phone Number ID is required';
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[0-9]{10,15}$/.test(formData.phoneNumber.replace(/[^0-9]/g, ''))) {
      newErrors.phoneNumber = 'Phone number must be 10-15 digits';
    }

    if (!formData.displayPhoneNumber) {
      newErrors.displayPhoneNumber = 'Display phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const submitData = { ...formData, wabaId };
      if (isEditMode && phoneNumber?._id) {
        submitData._id = phoneNumber._id;
      }
      onSave(submitData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content phone-number-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Phone Number' : 'Add Phone Number'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div style={{ padding: '12px 20px', background: '#fef3c7', borderBottom: '1px solid #fde68a', fontSize: '13px', color: '#92400e' }}>
          💡 Save operation will be performed on the server in production. This is a dev preview.
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* TABS */}
          <div className="modal-tabs" style={{ marginBottom: '20px', display: 'flex', gap: '10px', borderBottom: '2px solid #e5e7eb' }}>
            <button
              type="button"
              className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
              style={{
                padding: '10px 15px',
                background: activeTab === 'basic' ? '#3b82f6' : 'transparent',
                color: activeTab === 'basic' ? 'white' : '#6b7280',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: activeTab === 'basic' ? '600' : '500'
              }}
            >
              Basic Info
            </button>
            {isEditMode && (
              <>
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'status' ? 'active' : ''}`}
                  onClick={() => setActiveTab('status')}
                  style={{
                    padding: '10px 15px',
                    background: activeTab === 'status' ? '#3b82f6' : 'transparent',
                    color: activeTab === 'status' ? 'white' : '#6b7280',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: activeTab === 'status' ? '600' : '500'
                  }}
                >
                  Status & Quality
                </button>
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'metrics' ? 'active' : ''}`}
                  onClick={() => setActiveTab('metrics')}
                  style={{
                    padding: '10px 15px',
                    background: activeTab === 'metrics' ? '#3b82f6' : 'transparent',
                    color: activeTab === 'metrics' ? 'white' : '#6b7280',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: activeTab === 'metrics' ? '600' : '500'
                  }}
                >
                  Metrics
                </button>
              </>
            )}
          </div>

          {/* BASIC INFO TAB */}
          {activeTab === 'basic' && (
            <>
              {isEditMode && phoneNumber?.phoneNumberId && (
                <div className="form-group" style={{ padding: '12px', background: '#f3f4f6', borderRadius: '6px', marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '5px', fontWeight: '500' }}>Phone Number ID</label>
                  <div style={{ fontSize: '14px', color: '#111827', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {phoneNumber.phoneNumberId}
                  </div>
                </div>
              )}

              {!isEditMode && (
                <div className="form-group">
                  <label htmlFor="phoneNumberId">Phone Number ID *</label>
                  <input
                    type="text"
                    id="phoneNumberId"
                    name="phoneNumberId"
                    value={formData.phoneNumberId}
                    onChange={handleChange}
                    placeholder="Enter phone number ID from WhatsApp"
                    className={errors.phoneNumberId ? 'error' : ''}
                    disabled={loading}
                  />
                  {errors.phoneNumberId && <span className="error-message">{errors.phoneNumberId}</span>}
                  <span className="help-text">The unique identifier for this phone number (from WhatsApp Business Account)</span>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <PhoneInput
                  country={'in'}
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  inputProps={{
                    id: 'phoneNumber',
                    name: 'phoneNumber',
                    placeholder: '(201) 555-0123',
                    disabled: isEditMode || loading,
                    className: errors.phoneNumber ? 'error' : '',
                    style: { marginLeft: '37px' }
                  }}
                  enableAreaCodes={true}
                  disableDropdown={false}
                />
                {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
                <span className="help-text">Select country and enter phone number</span>
              </div>

              <div className="form-group">
                <label htmlFor="displayPhoneNumber">Display Phone Number *</label>
                <input
                  type="text"
                  id="displayPhoneNumber"
                  name="displayPhoneNumber"
                  value={formData.displayPhoneNumber}
                  onChange={handleChange}
                  placeholder="+1 (123) 456-7890"
                  className={errors.displayPhoneNumber ? 'error' : ''}
                />
                {errors.displayPhoneNumber && <span className="error-message">{errors.displayPhoneNumber}</span>}
                <span className="help-text">Formatted phone for display</span>
              </div>

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group checkbox">
                  <input
                    type="checkbox"
                    id="isPrimary"
                    name="isPrimary"
                    checked={formData.isPrimary}
                    onChange={(e) => setFormData({...formData, isPrimary: e.target.checked})}
                    disabled={loading}
                  />
                  <label htmlFor="isPrimary">Mark as Primary</label>
                </div>
                <div className="form-group checkbox">
                  <input
                    type="checkbox"
                    id="isDefault"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({...formData, isDefault: e.target.checked})}
                    disabled={loading}
                  />
                  <label htmlFor="isDefault">Set as Default</label>
                </div>
              </div>
            </>
          )}

          {/* STATUS & QUALITY TAB */}
          {activeTab === 'status' && isEditMode && phoneNumber && (
            <>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="status">Phone Status *</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-control"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="restricted">Restricted</option>
                </select>
                <span className="help-text">Change the operational status of this phone number</span>
              </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
              <div className="info-item" style={{ padding: '15px', background: '#f9fafb', borderRadius: '6px' }}>
                <span className="info-label" style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Verification</span>
                <span className="info-value" style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    background: phoneNumber.verificationStatus === 'VERIFIED' ? '#d1fae5' : '#fef3c7',
                    color: phoneNumber.verificationStatus === 'VERIFIED' ? '#065f46' : '#92400e',
                    fontSize: '13px'
                  }}>
                    {phoneNumber.verificationStatus || 'PENDING'}
                  </span>
                </span>
              </div>
              <div className="info-item" style={{ padding: '15px', background: '#f9fafb', borderRadius: '6px' }}>
                <span className="info-label" style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Quality Rating</span>
                <span className="info-value" style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    background: phoneNumber.phoneNumberQualityRating === 'GREEN' ? '#d1fae5' : phoneNumber.phoneNumberQualityRating === 'YELLOW' ? '#fef3c7' : '#fee2e2',
                    color: phoneNumber.phoneNumberQualityRating === 'GREEN' ? '#065f46' : phoneNumber.phoneNumberQualityRating === 'YELLOW' ? '#92400e' : '#7f1d1d',
                    fontSize: '13px'
                  }}>
                    {phoneNumber.phoneNumberQualityRating || 'UNKNOWN'}
                  </span>
                </span>
              </div>
              <div className="info-item" style={{ padding: '15px', background: '#f9fafb', borderRadius: '6px' }}>
                <span className="info-label" style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Name Status</span>
                <span className="info-value" style={{ fontSize: '14px', color: '#111827' }}>
                  {phoneNumber.nameStatus || 'NONE'}
                </span>
              </div>
              <div className="info-item" style={{ padding: '15px', background: '#f9fafb', borderRadius: '6px', gridColumn: '1 / -1' }}>
                <span className="info-label" style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>Last Synced</span>
                <span className="info-value" style={{ fontSize: '14px', color: '#111827' }}>
                  {phoneNumber.syncedAt ? new Date(phoneNumber.syncedAt).toLocaleString() : 'Never'}
                </span>
              </div>
            </div>
            </>
          )}

          {/* METRICS TAB */}
          {activeTab === 'metrics' && isEditMode && phoneNumber && (
            <div>
              <h4 style={{ marginBottom: '15px', fontSize: '14px', fontWeight: '600' }}>Message Statistics</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
                <div style={{ padding: '15px', background: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '5px' }}>Today Sent</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e3a8a' }}>{phoneNumber.messagesSentToday || 0}</div>
                </div>
                <div style={{ padding: '15px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '12px', color: '#166534', marginBottom: '5px' }}>Today Received</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#15803d' }}>{phoneNumber.messagesReceivedToday || 0}</div>
                </div>
                <div style={{ padding: '15px', background: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                  <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '5px' }}>Total Sent</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{phoneNumber.totalMessagesSent || 0}</div>
                </div>
                <div style={{ padding: '15px', background: '#fef3f2', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                  <div style={{ fontSize: '12px', color: '#9a3412', marginBottom: '5px' }}>Total Received</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#ea580c' }}>{phoneNumber.totalMessagesReceived || 0}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>Last Message Sent</span>
                  <div style={{ fontSize: '13px', color: '#111827', marginTop: '4px' }}>
                    {phoneNumber.lastMessageSentAt ? new Date(phoneNumber.lastMessageSentAt).toLocaleString() : 'Never'}
                  </div>
                </div>
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>Last Message Received</span>
                  <div style={{ fontSize: '13px', color: '#111827', marginTop: '4px' }}>
                    {phoneNumber.lastMessageReceivedAt ? new Date(phoneNumber.lastMessageReceivedAt).toLocaleString() : 'Never'}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Processing...' : (isEditMode ? 'Update Phone Number' : 'Add Phone Number')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PhoneNumberModal;

